import React from "react";
import { EmptyState, EmptyStateProps } from "../../../components/ui";
import { AccountName } from "../../users";
import type { SignupActivity } from "../types/dashboard.types";

/**
 * What a new arrival's row shows: where they are, and the two ways to reach
 * them. Not the plan — everyone lands on the same free fortnight, so a Plan
 * column here would be the same word five times.
 */
const COLUMNS = [
  { heading: "Account", width: "w-[24%]" },
  { heading: "Location", width: "w-[22%]" },
  { heading: "Phone number", width: "w-[18%]" },
  { heading: "Email", width: "w-[22%]" },
  { heading: "Date", width: "w-[14%]" },
];

interface SignupActivityTableProps {
  signups: SignupActivity[];
  /** Stands in for the body when there is nothing in the feed. */
  empty: EmptyStateProps;
  /** Opens the account behind the row — the whole row is the target. */
  onSelect?: (signup: SignupActivity) => void;
}

/** The latest signups, newest first — five of them at most; the cap is the
 *  service's, see `RECENT_ACTIVITY_COUNT`. */
const SignupActivityTable: React.FC<SignupActivityTableProps> = ({
  signups,
  empty,
  onSelect,
}) => {
  const cellClasses = "px-4 sm:px-6 py-4 text-[14px] text-ink";

  return (
    <div className="overflow-x-auto custom-scrollbar-overlay">
      <table className="w-full table-fixed border-collapse text-left min-w-[900px]">
        <colgroup>
          {COLUMNS.map((column) => (
            <col key={column.heading} className={column.width} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <th
                key={column.heading}
                scope="col"
                className="bg-white px-4 sm:px-6 py-3 text-[13px] font-normal text-ink-muted shadow-[inset_0_-1px_0_#E9EDF1]"
              >
                {column.heading}
              </th>
            ))}
          </tr>
        </thead>

        {signups.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={COLUMNS.length}>
                <EmptyState {...empty} />
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            {signups.map((signup) => (
              <tr
                key={signup.userId}
                onClick={() => onSelect?.(signup)}
                className={`border-b border-line last:border-0 transition-colors ${
                  onSelect ? "hover:bg-[#F8FEFF] cursor-pointer" : ""
                }`}
              >
                <td className="px-4 sm:px-6 py-4">
                  <AccountName name={signup.accountName} />
                </td>
                <td className={`${cellClasses} truncate`}>{signup.location}</td>
                <td className={`${cellClasses} whitespace-nowrap`}>
                  {signup.phone}
                </td>
                <td className={`${cellClasses} truncate`}>{signup.email}</td>
                <td className={`${cellClasses} whitespace-nowrap`}>
                  {signup.date}
                </td>
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
};

export default SignupActivityTable;
